import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { PageHeader } from "@/components/PageStates";
import { Button } from "@/components/ui/button";
import { records, formatDate, ledger } from "@/lib/mock-data";
import { useAppStore } from "@/lib/app-store";

export const Route = createFileRoute("/app/records/$id")({
  component: RecordDetail,
});

function RecordDetail() {
  const { id } = Route.useParams();
  const navigate = useNavigate();
  const { role, user } = useAppStore();
  const record = records.find((r) => r.id === id);

  if (!record) {
    return (
      <div>
        <PageHeader title="记录详情" onBack={() => navigate({ to: "/app/records" })} />
        <div className="px-6 pt-10 text-center" style={{ color: "var(--muted)" }}>
          未找到该记录
        </div>
      </div>
    );
  }

  const linkedLedger = ledger.filter((l) => l.recordId === record.id);
  const createdWithin24h =
    Date.now() - new Date(record.createdAt).getTime() < 86400000;
  const isOwner = user?.nickname === record.createdBy;
  const canEdit =
    role === "admin" || (role === "editor" && isOwner && createdWithin24h);

  return (
    <div className="pb-8">
      <PageHeader title="记录详情" onBack={() => navigate({ to: "/app/records" })} />
      <div className="space-y-4 px-4 pt-4">
        <section className="rounded-[var(--r-lg)] border bg-surface p-4">
          <p className="text-[var(--fs-md)] font-medium leading-relaxed">
            {record.description}
          </p>
          <div
            className="mt-3 grid grid-cols-2 gap-3 text-[var(--fs-sm)]"
            style={{ color: "var(--muted)" }}
          >
            <div>
              <span>类别</span>
              <p className="mt-0.5 text-fg">{record.category}</p>
            </div>
            <div>
              <span>点数</span>
              <p className="mt-0.5 text-fg">
                {record.kind === "scored"
                  ? `+${record.points}`
                  : record.kind === "praise"
                    ? "口头表扬"
                    : "提醒"}
              </p>
            </div>
            <div>
              <span>过期</span>
              <p className="mt-0.5 text-fg">
                {record.expiresAt ? formatDate(record.expiresAt) : "—"}
              </p>
            </div>
            <div>
              <span>创建</span>
              <p className="mt-0.5 text-fg">
                {formatDate(record.createdAt)} · {record.createdBy}
              </p>
            </div>
          </div>
          {record.tags && record.tags.length > 0 && (
            <div className="mt-3 flex flex-wrap gap-1.5">
              {record.tags.map((t) => (
                <span
                  key={t}
                  className="rounded-full px-2 py-0.5 text-[var(--fs-xs)]"
                  style={{ background: "var(--surface-2)" }}
                >
                  #{t}
                </span>
              ))}
            </div>
          )}
          {record.notes && (
            <p
              className="mt-3 rounded-[var(--r-sm)] p-2 text-[var(--fs-sm)]"
              style={{ background: "var(--surface-2)", color: "var(--muted)" }}
            >
              {record.notes}
            </p>
          )}
        </section>

        <section>
          <h2 className="mb-2 px-1 text-[var(--fs-sm)] font-semibold">
            关联的台账
          </h2>
          {linkedLedger.length === 0 ? (
            <p
              className="rounded-[var(--r-md)] border border-dashed px-4 py-6 text-center text-[var(--fs-sm)]"
              style={{ color: "var(--muted)" }}
            >
              这条记录暂未产生点数流转
            </p>
          ) : (
            <ul className="space-y-2">
              {linkedLedger.map((l) => (
                <li
                  key={l.id}
                  className="flex items-center justify-between rounded-[var(--r-md)] border bg-surface p-3"
                >
                  <div>
                    <p className="text-[var(--fs-sm)] font-medium">{l.source}</p>
                    <p
                      className="text-[var(--fs-xs)]"
                      style={{ color: "var(--muted)" }}
                    >
                      {formatDate(l.createdAt)}
                    </p>
                  </div>
                  <span
                    className="font-semibold"
                    style={{
                      color: l.delta > 0 ? "var(--success)" : "var(--danger)",
                    }}
                  >
                    {l.delta > 0 ? "+" : ""}
                    {l.delta}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </section>

        <section>
          <h2 className="mb-2 px-1 text-[var(--fs-sm)] font-semibold">编辑历史</h2>
          <p
            className="rounded-[var(--r-md)] border bg-surface px-3 py-2 text-[var(--fs-sm)]"
            style={{ color: "var(--muted)" }}
          >
            创建于 {formatDate(record.createdAt)} · 未修改
          </p>
        </section>

        {canEdit ? (
          <Button variant="outline" className="w-full">
            编辑记录
          </Button>
        ) : (
          <p
            className="text-center text-[var(--fs-xs)]"
            style={{ color: "var(--muted)" }}
          >
            只能编辑自己 24 小时内创建的记录
          </p>
        )}
      </div>
    </div>
  );
}
